import { CSSProperties, Fragment, useRef, useState } from 'react';

import { FormPopoverComments } from '../FormPopoverComments';

import { FormCommentActionBar } from '../FormCommentActionBar';

import { Popover } from 'components-antd';
import {
  AddFormCommentType,
  CommentUsersType,
  EditFormCommentType,
  FormCommentThread,
  FieldInfoType,
  ListType,
} from 'types';

import styles from './styles.module.scss';
import classNames from 'classnames';

import { CommentAvatarIndicator } from './CommentAvatarIndicator';
import { useOutsideClick } from 'hooks';
import { shouldClosePopover } from './helper';
import { useCommentPermissions } from './hooks/useCommentPermissions';
import { useCommentPopoverManager } from './hooks/useCommentPopoverManager';

interface FormCommentPopoverProps {
  isNewThread: boolean;
  zIndex?: number;
  tooltipStyles?: CSSProperties;
  commentUsers?: CommentUsersType;
  fieldInfo?: FieldInfoType;
  threads?: FormCommentThread[];
  handleGetFormComments?: () => void;
  handleAddFormComment: (
    payload: AddFormCommentType,
    setSending: (isSending: boolean) => void,
  ) => void;
  handleEditFormComment?: (
    payload: EditFormCommentType,
    setEditingMode: (editingMode: boolean) => void,
    setSending: (isSending: boolean) => void,
  ) => void;
  handleDeleteFormComment?: (formCommentId: number) => void;
  handleToggleResolveThread?: (
    threadId: number,
    setResolving: (resolving: boolean) => void,
  ) => void;
  dynamicComment?: boolean;
  isOpenPopover?: boolean;
  fieldId?: string;
  hideTooltipTitle?: boolean;
  onCommentClick?: () => void;
  listType?: ListType;
  defaultOpenPopover?: boolean;
}

export const FormCommentPopover = ({
  isNewThread,
  zIndex,
  tooltipStyles,
  commentUsers = {},
  fieldInfo,
  threads = [],
  handleGetFormComments,
  handleAddFormComment,
  handleEditFormComment,
  handleDeleteFormComment,
  handleToggleResolveThread,
  dynamicComment,
  defaultOpenPopover,
  hideTooltipTitle,
  onCommentClick,
  listType,
}: FormCommentPopoverProps) => {
  const [isSending, setSending] = useState<boolean>(false);

  const {
    notifyOptions,
    notifiedUsers,
    permittedUsers,
    setNotifiedUsers,
    setPermittedUsers,
    permissionOptions,
  } = useCommentPermissions(commentUsers);

  const { isPopoverOpen, setPopoverOpen, isReady, offset, activeKey, setActiveKey } =
    useCommentPopoverManager(defaultOpenPopover, listType);

  const popoverRef = useRef(null);
  const isFormCommentsList = listType === 'FormComments';

  useOutsideClick([popoverRef], (event) => {
    if (isPopoverOpen && shouldClosePopover(event)) {
      setPopoverOpen(false);
    }
  });

  const renderFormCommentPopover = () => {
    return (
      <FormPopoverComments
        threads={threads}
        commentUsers={commentUsers}
        notifyOptions={notifyOptions}
        permissionOptions={permissionOptions}
        permittedUsers={permittedUsers}
        setPermittedUsers={setPermittedUsers}
        notifiedUsers={notifiedUsers}
        setNotifiedUsers={setNotifiedUsers as any}
        isSending={isSending}
        setSending={setSending}
        activeKey={activeKey}
        setActiveKey={setActiveKey}
        handleAddFormComment={handleAddFormComment}
        handleEditFormComment={handleEditFormComment}
        handleDeleteFormComment={handleDeleteFormComment}
        handleToggleResolveThread={handleToggleResolveThread}
        dynamicComment
      />
    );
  };

  const renderNewThread = () => {
    return (
      <FormCommentActionBar
        isNewThread={true}
        isSending={isSending}
        setSending={setSending}
        commentUsers={commentUsers}
        notifyOptions={notifyOptions}
        permissionOptions={permissionOptions}
        notifiedUsers={notifiedUsers}
        setNotifiedUsers={setNotifiedUsers as any}
        permittedUsers={permittedUsers}
        setPermittedUsers={setPermittedUsers}
        handleAddFormComment={handleAddFormComment}
        fieldInfo={fieldInfo}
      />
    );
  };

  const renderPopover = () => {
    return (
      <Fragment>
        <Popover
          key={`${threads[0].Id}-pdfField-thread`}
          open={isPopoverOpen && isReady}
          onOpenChange={(open: boolean) => {
            if (!isFormCommentsList) {
              setPopoverOpen(open);
            }

            if (open) {
              isFormCommentsList && setPopoverOpen(open);
              !dynamicComment && handleGetFormComments?.();
              onCommentClick?.();
            } else {
              !isFormCommentsList && setActiveKey('');
            }
          }}
          destroyTooltipOnHide
          placement="bottomLeft"
          overlayClassName={`${styles.popoverContent} comment-popover-overlay`}
          zIndex={zIndex}
          content={renderFormCommentPopover()}
          getPopupContainer={(e) => e}
          trigger="click"
          autoAdjustOverflow={false}
          align={{ offset: [offset, 0], overflow: { adjustX: false, adjustY: false } }}
        >
          <div
            style={tooltipStyles}
            className={classNames(
              styles.commentIndicator,
              {
                [styles.dynamicFormCommentIndicator]: dynamicComment,
              },
              'form-comment-indicator',
            )}
            ref={isFormCommentsList ? popoverRef : null}
          >
            {!isPopoverOpen && (
              <CommentAvatarIndicator
                threads={threads}
                dynamicComment={dynamicComment}
                hideTooltipTitle={hideTooltipTitle}
                commentUsers={commentUsers}
              />
            )}
          </div>
        </Popover>
      </Fragment>
    );
  };

  if (isNewThread) return renderNewThread();
  if (!threads.length) return null;
  return renderPopover();
};
